import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ArrowUpRight } from 'lucide-react';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [pathname, setPathname] = useState('/');

  useEffect(() => {
    setPathname(window.location.pathname);
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const menuItems = [
    { name: 'Home', href: '/' },
    { name: 'Fleet & Ground', href: '/ground' },
    { name: 'Equipment', href: '/equipment' },
    { name: 'Cross-Border', href: '/cross-border' },
    { name: 'Global & Tech', href: '/global-tech' },
    { name: 'Contact', href: '/contact' },
  ];

  const isActive = (href: string) => {
    if (href === '/') return pathname === '/';
    return pathname.startsWith(href);
  };

  return (
    <>
      {/* Scroll Progress Bar */}
      <motion.div 
        className="fixed top-0 left-0 right-0 h-1 bg-amber z-[60] origin-left"
        initial={{ scaleX: 0 }}
        animate={{ scaleX: typeof window !== 'undefined' ? window.scrollY / (document.documentElement.scrollHeight - window.innerHeight) : 0 }}
      />

      <motion.nav 
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="fixed top-0 left-0 right-0 z-50 bg-bone shadow-lg"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo - Left */}
            <a href="/" className="flex items-center gap-3 group">
              <div className="h-14 w-auto transition-transform group-hover:scale-105">
                <img 
                  src="/logo.png" 
                  alt="Quinte Freight" 
                  className="h-full w-auto object-contain"
                />
              </div>
            </a>

            {/* Desktop Menu - Center */}
            <div className="hidden lg:flex items-center gap-8">
              {menuItems.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  className={`font-body font-semibold text-sm transition-colors relative group ${
                    isActive(item.href) ? 'text-forest font-bold' : 'text-forest/80 hover:text-forest'
                  }`}
                >
                  {item.name}
                  <span className={`absolute -bottom-1 left-0 h-0.5 transition-all duration-300 ${
                    isActive(item.href) ? 'w-full bg-forest' : 'w-0 group-hover:w-full bg-forest'
                  }`} />
                </a>
              ))}
            </div>

            {/* CTAs - Right */}
            <div className="hidden lg:flex items-center gap-4">
              <a 
                href="/quote" 
                className="text-sm font-bold px-5 py-2.5 rounded-full transition-all flex items-center gap-2 btn-primary"
              >
                Get a Quote
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden p-2 transition-colors text-forest hover:text-amber"
              aria-label="Toggle menu"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu - Full Screen Overlay */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="lg:hidden fixed inset-0 top-20 bg-forest z-40"
            >
              <div className="flex flex-col items-center justify-center h-full gap-8 pb-20">
                {menuItems.map((item, index) => (
                  <motion.a
                    key={item.name}
                    href={item.href}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    onClick={() => setIsOpen(false)}
                    className={`font-display font-bold text-3xl transition-colors ${
                      isActive(item.href) ? 'text-amber underline underline-offset-8' : 'text-white hover:text-amber'
                    }`}
                  >
                    {item.name}
                  </motion.a>
                ))}
                <motion.a
                  href="/quote"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  onClick={() => setIsOpen(false)}
                  className="btn-primary text-lg mt-4"
                >
                  Get a Quote
                </motion.a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>
    </>
  );
};

export default Navigation;
